import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
    Clock, Target, RotateCcw, Award, Zap, Flame, Skull,
    User, LogOut, TrendingUp, Coins, Calendar, Gift, Bell,
    Settings, X, ChevronDown, ChevronUp, AlertCircle, Pause,
    Sparkles, Rocket, Shield, Crown, Star
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

let PLAYFAB_TITLE_ID = '';

const SHOP_ICON_MAP = {
    boost_1_5: Rocket,
    boost_2: Zap,
    streak_freeze: Shield,
    hardness_potion: Skull,
    genie_1: Sparkles,
    genie_2: Star,
    genie_3: Crown,
    iso_certificate: Award,
};

const SHOP_COLOR_MAP = {
    boost_1_5: 'from-[#7BC69A] to-[#5DB88A]',
    boost_2: 'from-blue-400 to-cyan-500',
    streak_freeze: 'from-cyan-400 to-blue-500',
    hardness_potion: 'from-red-300 to-purple-400',
    genie_1: 'from-yellow-400 to-yellow-600',
    genie_2: 'from-orange-400 to-red-500',
    genie_3: 'from-purple-300 to-pink-400',
    iso_certificate: 'from-amber-400 via-yellow-500 to-amber-400',
};

const DIFF_ICON_MAP = {
    easy: Target,
    medium: Zap,
    hard: Flame,
    insane: Skull,
    certification: Award,
};

// ─────────────────────────────────────────────────────────────────────────────
// INJECT GLOBAL STYLES
// ─────────────────────────────────────────────────────────────────────────────

const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700;800&display=swap');

body { font-family: 'Calibri', 'Trebuchet MS', Arial, sans-serif; -webkit-font-smoothing: antialiased; }
h1,h2,h3,h4,h5,h6,.header-text { font-family: 'Century Gothic','AppleGothic','Avant Garde',sans-serif; font-weight:700; letter-spacing:-0.02em; }

@keyframes fadeIn { from{opacity:0} to{opacity:1} }
@keyframes slideUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
@keyframes gentlePulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
@keyframes streakBarFlash { 0%{filter:brightness(1) saturate(1);transform:scale(1)} 50%{filter:brightness(1.3) saturate(1.3);transform:scale(1.05)} 100%{filter:brightness(1) saturate(1);transform:scale(1)} }
@keyframes flameYellowFill { 0%{fill:currentColor} 50%{fill:#F5CE1E} 100%{fill:currentColor} }
@keyframes scrollUp { from{transform:translateY(100vh)} to{transform:translateY(-100%)} }
@keyframes drawAmpersand { 0%{stroke-dashoffset:1000} 45%{stroke-dashoffset:0} 55%{stroke-dashoffset:0} 100%{stroke-dashoffset:-1000} }

.animate-fadeIn { animation: fadeIn 2s ease-in; }
.animate-slideUp { animation: slideUp 1.5s ease-out 0.5s both; }
.animate-gentlePulse { animation: gentlePulse 2s ease-in-out infinite; }
.animate-streakBarFlash { animation: streakBarFlash 1.2s ease-in-out; }
.animate-flameYellowFill { animation: flameYellowFill 1.2s ease-in-out; }
.animate-ampersand { animation: drawAmpersand 3s ease-in-out infinite; }
`;

if (typeof document !== 'undefined') {
    const s = document.createElement('style');
    s.textContent = globalStyles;
    document.head.appendChild(s);
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFETTI
// ─────────────────────────────────────────────────────────────────────────────

const getConfettiColors = () => {
    const c = window.appColors;
    return c
        ? [c.confetti_1, c.confetti_2, c.confetti_3, c.confetti_4, c.confetti_5, c.confetti_6, c.confetti_7, c.confetti_8]
        : ['#F5CE1E', '#EA6133', '#46A66E', '#227280', '#F3A214', '#249B9F', '#178169', '#C87533'];
};

const createConfetti = () => {
    const colors = getConfettiColors();
    const elements = [];
    for (let i = 0; i < 50; i++) {
        const el = document.createElement('div');
        el.style.cssText = `position:fixed;width:10px;height:10px;border-radius:50%;left:50%;top:50%;z-index:9999;pointer-events:none;`;
        el.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        document.body.appendChild(el);
        const angle = (Math.PI * 2 * i) / 50;
        const v = 200 + Math.random() * 200;
        el.animate([
            { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
            { transform: `translate(${Math.cos(angle) * v}px,${Math.sin(angle) * v}px) rotate(${Math.random() * 720}deg)`, opacity: 0 }
        ], { duration: 1500, easing: 'cubic-bezier(0,.9,.57,1)' });
        elements.push(el);
    }
    setTimeout(() => elements.forEach(el => el.remove()), 1500);
};

const createCertificateConfetti = () => {
    const colors = getConfettiColors();
    const elements = [];
    let stopped = false;

    const wave = (delay = 0) => {
        setTimeout(() => {
            if (stopped) return;
            for (let i = 0; i < 100; i++) {
                const el = document.createElement('div');
                const size = Math.random() * 15 + 10;
                el.style.cssText = `position:fixed;width:${size}px;height:${size}px;left:${Math.random() * 100}%;top:-50px;z-index:9999;pointer-events:none;border-radius:${Math.random() > .5 ? '50%' : '0%'};opacity:1;`;
                el.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
                document.body.appendChild(el);
                const drift = (Math.random() - 0.5) * 400;
                const dur = 4000 + Math.random() * 2000;
                el.animate([
                    { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
                    { transform: `translate(${drift}px,${window.innerHeight + 150}px) rotate(${Math.random() * 1080}deg)`, opacity: 0.7 }
                ], { duration: dur, easing: 'cubic-bezier(0.25,0.46,0.45,0.94)' });
                elements.push(el);
            }
        }, delay);
    };

    for (let i = 0; i <= 21; i++) wave(i * 250);

    return {
        elements,
        stop: () => { stopped = true; }
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// PLAYFAB API
// ─────────────────────────────────────────────────────────────────────────────

const PlayFabAPI = {
    base: `https://${PLAYFAB_TITLE_ID}.playfabapi.com`,

    async call(endpoint, body, sessionTicket = null) {
        const headers = { 'Content-Type': 'application/json' };
        if (sessionTicket) headers['X-Authentication'] = sessionTicket;
        const r = await fetch(`${this.base}${endpoint}`, {
            method: 'POST', headers, body: JSON.stringify(body)
        });
        return r.json();
    },

    login: (u, p) => PlayFabAPI.call('/Client/LoginWithPlayFab', { TitleId: PLAYFAB_TITLE_ID, Username: u, Password: p }),
    register: (u, p, dn, email) => PlayFabAPI.call('/Client/RegisterPlayFabUser', { TitleId: PLAYFAB_TITLE_ID, Username: u, Password: p, DisplayName: dn || u, Email: email || undefined, RequireBothUsernameAndEmail: false }),
    sendPasswordReset: (email) => PlayFabAPI.call('/Client/SendAccountRecoveryEmail', { TitleId: PLAYFAB_TITLE_ID, Email: email }),
    updateStats: (t, stats) => PlayFabAPI.call('/Client/UpdatePlayerStatistics', { Statistics: stats }, t),
    getLeaderboard: (t, name, max = 100) => PlayFabAPI.call('/Client/GetLeaderboard', { StatisticName: name, StartPosition: 0, MaxResultsCount: max }, t),
    getUserData: (t, keys) => PlayFabAPI.call('/Client/GetUserData', { Keys: keys }, t),
    updateUserData: (t, data) => PlayFabAPI.call('/Client/UpdateUserData', { Data: data }, t),
    updateDisplayName: (t, name) => PlayFabAPI.call('/Client/UpdateUserTitleDisplayName', { DisplayName: name }, t),
};

// ─────────────────────────────────────────────────────────────────────────────
// EXCEL LOADER HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const fetchXLSX = async (path) => {
    const r = await fetch(path);
    if (!r.ok) throw new Error(`Failed to fetch ${path}: ${r.status}`);
    const buf = await r.arrayBuffer();
    return XLSX.read(buf, { type: 'array' });
};

const sheetToMap = (wb, sheetName, keyCol, valueCol) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return {};
    const rows = XLSX.utils.sheet_to_json(ws);
    const map = {};
    rows.forEach(row => {
        if (row[keyCol] !== undefined && row[valueCol] !== undefined) {
            map[row[keyCol]] = row[valueCol];
        }
    });
    return map;
};

const sheetToArray = (wb, sheetName) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return [];
    return XLSX.utils.sheet_to_json(ws);
};

// ─────────────────────────────────────────────────────────────────────────────
// QUESTION LOADER
// ─────────────────────────────────────────────────────────────────────────────

const loadQuestionsFromFile = async (filePath) => {
    try {
        const wb = await fetchXLSX(filePath);
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);

        const filtered = rows.filter(r =>
            (r.Question || r.question) &&
            (r.OptionA || r.optionA) && (r.OptionB || r.optionB) &&
            (r.OptionC || r.optionC) && (r.OptionD || r.optionD)
        );

        return filtered.map(r => {
            let correct = r.Correct !== undefined ? r.Correct : r.correct;
            if (typeof correct === 'string') correct = parseInt(correct, 10);
            return {
                q: r.Question || r.question,
                options: [r.OptionA || r.optionA, r.OptionB || r.optionB, r.OptionC || r.optionC, r.OptionD || r.optionD],
                correct: typeof correct === 'number' && !isNaN(correct) && correct >= 0 && correct <= 3 ? correct : -1,
            };
        }).filter(q => q.q && q.options.every(o => o) && q.correct !== -1);
    } catch (e) {
        console.error('Error loading questions from', filePath, e);
        return [];
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// SHUFFLE
// ─────────────────────────────────────────────────────────────────────────────

const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function App() {

    // ── Config state ────────────────────────────────────────────────────────
    const [appText, setAppText] = useState({});
    const [colors, setColors] = useState({});
    const [difficulties, setDifficulties] = useState({});
    const [shopItems, setShopItems] = useState([]);
    const [assetPaths, setAssetPaths] = useState({});
    const [configLoaded, setConfigLoaded] = useState(false);

    // ── Question pools ───────────────────────────────────────────────────────
    const [questionPools, setQuestionPools] = useState({});
    const [dailyPool, setDailyPool] = useState([]);
    const [questionsLoaded, setQuestionsLoaded] = useState(false);
    const [minLoadingComplete, setMinLoadingComplete] = useState(false);

    // ── Auth ────────────────────────────────────────────────────────────────
    const [gameState, setGameState] = useState('loading');
    const [authMode, setAuthMode] = useState('login');
    const [user, setUser] = useState(null);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [forgotSuccess, setForgotSuccess] = useState('');
    const [isDevUser, setIsDevUser] = useState(false);
    const [currentDisplayName, setCurrentDisplayName] = useState('');

    // ── Game ─────────────────────────────────────────────────────────────────
    const [difficulty, setDifficulty] = useState(null);
    const [questionSet, setQuestionSet] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null); // eslint-disable-line no-unused-vars
    const [showResult, setShowResult] = useState(false);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [totalTime, setTotalTime] = useState(0); // eslint-disable-line no-unused-vars
    const [streak, setStreak] = useState(0);
    const [maxStreak, setMaxStreak] = useState(0);
    const [showStreakAnim, setShowStreakAnim] = useState(false);
    const [gameHistory, setGameHistory] = useState([]);
    const [gamePaused, setGamePaused] = useState(false);
    const [showQuitConfirm, setShowQuitConfirm] = useState(false);
    const [resultMessage, setResultMessage] = useState('');
    const [recommendedDiff, setRecommendedDiff] = useState(null);

    // ── Player data ──────────────────────────────────────────────────────────
    const [coins, setCoins] = useState(0);
    const [dailyStreak, setDailyStreak] = useState(0);
    const [lastPlayDate, setLastPlayDate] = useState(null);
    const [dailyCompleted, setDailyCompleted] = useState(false);
    const [inventory, setInventory] = useState({ streak_freeze: 0 });
    const [activeBoosts, setActiveBoosts] = useState([]);
    const [myRank, setMyRank] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [globalLeaderboard, setGlobalLeaderboard] = useState([]);

    // ── UI toggles ───────────────────────────────────────────────────────────
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [leaderboardDiff, setLeaderboardDiff] = useState('easy');
    const [showShop, setShowShop] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showDailyQuestion, setShowDailyQuestion] = useState(false);
    const [dailyQuestion, setDailyQuestion] = useState(null);
    const [dailyQuestionChecked, setDailyQuestionChecked] = useState(false);
    const [showReadyScreen, setShowReadyScreen] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showCredits, setShowCredits] = useState(false);
    const [creditsComplete, setCreditsComplete] = useState(false);
    const [showSkipButton, setShowSkipButton] = useState(false);
    const [showHardnessNotif, setShowHardnessNotif] = useState(false);
    const [showIncompatWarn, setShowIncompatWarn] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [newDisplayName, setNewDisplayName] = useState('');
    const [devAwardAmount, setDevAwardAmount] = useState('');

    // ─────────────────────────────────────────────────────────────────────────
    // HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    const t = useCallback((key, fallback = '') => appText[key] || fallback, [appText]);
    const c = useCallback((key, fallback = '#F3A214') => colors[key] || fallback, [colors]);

    const addNotification = useCallback((message) => {
        if (!notificationsEnabled) return;
        const id = Date.now() + Math.random();
        setNotifications(prev => [...prev, { id, message }]);
        setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 5000);
    }, [notificationsEnabled]);

    const getActiveMultiplier = useCallback(() => {
        if (activeBoosts.some(b => b.id === 'genie_3')) return 4;
        if (activeBoosts.some(b => b.id === 'hardness_potion')) return 3;
        if (activeBoosts.some(b => b.id === 'genie_2')) return 2;
        if (activeBoosts.some(b => b.id === 'boost_2')) return 2;
        if (activeBoosts.some(b => b.id === 'genie_1')) return 1.5;
        if (activeBoosts.some(b => b.id === 'boost_1_5')) return 1.5;
        return 1;
    }, [activeBoosts]);

    const isHardnessPotionActive = useCallback(() =>
        activeBoosts.some(b => b.id === 'hardness_potion' || b.id === 'genie_3'),
        [activeBoosts]);

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD MASTER CONFIG
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const wb = await fetchXLSX('./master_config.xlsx');

                // app text
                const text = sheetToMap(wb, 'app', 'Key', 'Value');
                setAppText(text);

                if (text.playfab_unique_id) {
                    PLAYFAB_TITLE_ID = text.playfab_unique_id;
                    PlayFabAPI.base = `https://${PLAYFAB_TITLE_ID}.playfabapi.com`;
                }

                // update page title
                if (text.app_name) document.title = text.app_name;

                // colors
                const colorMap = sheetToMap(wb, 'colors', 'Element', 'ColorCode');
                setColors(colorMap);
                window.appColors = colorMap;

                // difficulties
                const diffRows = sheetToArray(wb, 'difficulties');
                const diffMap = {};
                diffRows.filter(r => r.id).forEach(r => {
                    diffMap[r.id] = {
                        id: r.id,
                        name: r.name,
                        time: Number(r.time_seconds),
                        questions: Number(r.questions),
                        multiplier: Number(r.multiplier),
                        leaderboard: r.has_leaderboard === 'yes' ? r.leaderboard_name : null,
                        icon: DIFF_ICON_MAP[r.id] || Target,
                        bgColor: colorMap[`${r.id}_mode`] || '#46A66E',
                    };
                });
                setDifficulties(diffMap);

                // shop items
                const shopRows = sheetToArray(wb, 'shop');
                const shop = shopRows.map(r => ({
                    id: r.id,
                    name: r.name,
                    price: Number(r.price),
                    duration: Number(r.duration_minutes) * 60000,
                    description: r.description,
                    warning: r.warning || '',
                    multiplier: Number(r.multiplier),
                    removesEasyMedium: r.removes_easy_medium === 'yes',
                    icon: SHOP_ICON_MAP[r.id] || Zap,
                    color: SHOP_COLOR_MAP[r.id] || 'from-gray-400 to-gray-500',
                }));
                setShopItems(shop);

                // assets
                const assetMap = sheetToMap(wb, 'assets', 'Key', 'Value');
                setAssetPaths(assetMap);

                setConfigLoaded(true);
            } catch (e) {
                console.error('Failed to load master_config.xlsx:', e);
                // fallback so app doesn't hang
                setConfigLoaded(true);
            }
        };
        loadConfig();
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // MINIMUM LOADING TIME (2 seconds)
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const timer = setTimeout(() => setMinLoadingComplete(true), 2000);
        return () => clearTimeout(timer);
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD QUESTIONS
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!configLoaded) return;
        const loadAll = async () => {
            const [easy, medium, hard, insane, cert, daily] = await Promise.all([
                loadQuestionsFromFile('./questions/questions_easy.xlsx'),
                loadQuestionsFromFile('./questions/questions_medium.xlsx'),
                loadQuestionsFromFile('./questions/questions_hard.xlsx'),
                loadQuestionsFromFile('./questions/questions_insane.xlsx'),
                loadQuestionsFromFile('./questions/questions_certification.xlsx'),
                loadQuestionsFromFile('./questions/questions_daily.xlsx'),
            ]);
            setQuestionPools({ easy, medium, hard, insane, certification: cert });
            setDailyPool(daily);
            setQuestionsLoaded(true);
        };
        loadAll();
    }, [configLoaded]);

    // ─────────────────────────────────────────────────────────────────────────
    // BOOST EXPIRY TICKER
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveBoosts(prev => prev.filter(b => b.expiresAt > Date.now()));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // AUTO LOGIN
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!questionsLoaded) return;
        const saved = localStorage.getItem('playfabUser');
        const savedRemember = localStorage.getItem('rememberMe') === 'true';
        if (savedRemember) {
            setRememberMe(true);
            setUsername(localStorage.getItem('savedUsername') || '');
            setPassword(localStorage.getItem('savedPassword') || '');
        }
        if (saved) {
            const userData = JSON.parse(saved);
            setUser(userData);
            setCurrentDisplayName(userData.displayName || userData.username);
            if (userData.username === 'IndigoBlack4320') setIsDevUser(true);
            loadPlayerData(userData);
            setGameState('menu');
        } else {
            setGameState('auth');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionsLoaded]);

    // ─────────────────────────────────────────────────────────────────────────
    // GAME TIMER
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (gameState !== 'playing' || showResult || gamePaused) return;
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            handleAnswer(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft, gameState, showResult, gamePaused]);

    // ─────────────────────────────────────────────────────────────────────────
    // HARDNESS NOTIFICATION
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (gameState === 'playing' && isHardnessPotionActive()) {
            setShowHardnessNotif(true);
            const timer = setTimeout(() => setShowHardnessNotif(false), 5000);
            return () => clearTimeout(timer);
        } else {
            setShowHardnessNotif(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState]);

    // ─────────────────────────────────────────────────────────────────────────
    // CREDITS SCROLL
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!showCredits) { document.body.style.overflow = 'auto'; return; }
        setCreditsComplete(false);
        setShowSkipButton(false);
        document.body.style.overflow = 'hidden';
        const stopTimer = setTimeout(() => setCreditsComplete(true), 90000 * 0.80);
        const returnTimer = setTimeout(() => { setShowCredits(false); document.body.style.overflow = 'auto'; }, 90000 * 0.80 + 10000);
        return () => { clearTimeout(stopTimer); clearTimeout(returnTimer); document.body.style.overflow = 'auto'; };
    }, [showCredits]);

    // ─────────────────────────────────────────────────────────────────────────
    // DAILY QUESTION CHECK
    // ─────────────────────────────────────────────────────────────────────────

    const checkDailyReset = useCallback(async (loadedLastPlayDate, loadedStreak, loadedDailyCompleted, loadedInventory, userData) => {
        const today = new Date().toISOString().slice(0, 10);
        let shouldShow = false;
        let newDailyQ = null;

        if (loadedLastPlayDate !== today) {
            if (dailyPool.length > 0) {
                newDailyQ = dailyPool[Math.floor(Math.random() * dailyPool.length)];
                shouldShow = true;
            }
            setDailyCompleted(false);
            setDailyQuestion(newDailyQ);
            if (userData) {
                try {
                    await PlayFabAPI.updateUserData(userData.sessionTicket, {
                        dailyQuestionCompleted: 'false',
                        dailyQuestionText: newDailyQ ? newDailyQ.q : '',
                        inventory: JSON.stringify(loadedInventory)
                    });
                } catch { }
            }
        } else {
            let savedText = null;
            try {
                if (userData) {
                    const res = await PlayFabAPI.getUserData(userData.sessionTicket, ['dailyQuestionText']);
                    savedText = res?.data?.Data?.dailyQuestionText?.Value || null;
                }
            } catch { }
            const matched = savedText && dailyPool.length > 0
                ? dailyPool.find(q => q.q === savedText)
                : (dailyPool.length > 0 ? dailyPool[Math.floor(Math.random() * dailyPool.length)] : null);
            setDailyQuestion(matched || null);
            shouldShow = !loadedDailyCompleted && matched !== null;
        }

        setDailyStreak(loadedStreak);
        setDailyCompleted(loadedDailyCompleted);
        setLastPlayDate(loadedLastPlayDate);
        setInventory(loadedInventory);
        setDailyQuestionChecked(true);
        if (shouldShow) setShowDailyQuestion(true);
    }, [dailyPool]);

    useEffect(() => {
        if (!questionsLoaded || dailyQuestionChecked) return;
        if (!user) { setDailyQuestionChecked(true); return; }
        const run = async () => {
            try {
                const result = await PlayFabAPI.getUserData(user.sessionTicket, ['dailyStreak', 'lastPlayDate', 'dailyQuestionCompleted', 'inventory']);
                let ls = 0, ld = null, lc = false, li = { streak_freeze: 0 };
                if (result.code === 200 && result.data?.Data) {
                    const d = result.data.Data;
                    if (d.dailyStreak) ls = parseInt(d.dailyStreak.Value);
                    if (d.lastPlayDate) ld = d.lastPlayDate.Value;
                    if (d.dailyQuestionCompleted) lc = d.dailyQuestionCompleted.Value === 'true';
                    if (d.inventory) li = JSON.parse(d.inventory.Value);
                }
                await checkDailyReset(ld, ls, lc, li, user);
            } catch (e) {
                console.error(e);
                setDailyQuestionChecked(true);
            }
        };
        run();
    }, [user, questionsLoaded, dailyQuestionChecked, checkDailyReset]);

    // ─────────────────────────────────────────────────────────────────────────
    // PLAYER DATA
    // ─────────────────────────────────────────────────────────────────────────

    const loadPlayerData = async (userData) => {
        try {
            const result = await PlayFabAPI.getUserData(userData.sessionTicket, ['dailyStreak', 'lastPlayDate', 'dailyQuestionCompleted', 'lastRank', 'coins', 'inventory', 'activeBoosts', 'personalLeaderboard']);
            if (result.code === 200 && result.data?.Data) {
                const d = result.data.Data;
                if (d.dailyStreak) setDailyStreak(parseInt(d.dailyStreak.Value));
                if (d.lastPlayDate) setLastPlayDate(d.lastPlayDate.Value);
                if (d.dailyQuestionCompleted) setDailyCompleted(d.dailyQuestionCompleted.Value === 'true');
                if (d.lastRank) setMyRank(parseInt(d.lastRank.Value));
                if (d.coins) setCoins(parseInt(d.coins.Value));
                if (d.inventory) setInventory(JSON.parse(d.inventory.Value));
                if (d.activeBoosts) {
                    const boosts = JSON.parse(d.activeBoosts.Value).filter(b => b.expiresAt > Date.now());
                    setActiveBoosts(boosts);
                }
                if (d.personalLeaderboard) setLeaderboard(JSON.parse(d.personalLeaderboard.Value));
            }
            await loadSettings(userData);
        } catch (e) {
            console.error('loadPlayerData failed:', e);
        }
    };

    const loadSettings = async (userData) => {
        try {
            const result = await PlayFabAPI.getUserData(userData.sessionTicket, ['notificationsEnabled', 'displayName']);
            if (result.code === 200 && result.data?.Data) {
                const d = result.data.Data;
                if (d.notificationsEnabled) setNotificationsEnabled(d.notificationsEnabled.Value === 'true');
                if (d.displayName) {
                    setUser(u => ({ ...u, displayName: d.displayName.Value }));
                    setCurrentDisplayName(d.displayName.Value);
                } else {
                    setCurrentDisplayName(userData.username);
                }
            }
        } catch { }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // AUTH
    // ─────────────────────────────────────────────────────────────────────────

    const handleLogin = async () => {
        setAuthError('');
        setAuthLoading(true);
        try {
            const result = await PlayFabAPI.login(username, password);
            if (result.code === 200 && result.data) {
                const userData = {
                    playFabId: result.data.PlayFabId,
                    username: username,
                    sessionTicket: result.data.SessionTicket,
                    displayName: username
                };
                setUser(userData);
                setCurrentDisplayName(username);
                if (username === 'IndigoBlack4320') setIsDevUser(true);
                localStorage.setItem('playfabUser', JSON.stringify(userData));
                if (rememberMe) {
                    localStorage.setItem('rememberMe', 'true');
                    localStorage.setItem('savedUsername', username);
                    localStorage.setItem('savedPassword', password);
                } else {
                    localStorage.removeItem('rememberMe');
                    localStorage.removeItem('savedUsername');
                    localStorage.removeItem('savedPassword');
                }
                await loadPlayerData(userData);
                setGameState('menu');
            } else {
                setAuthError(result.errorMessage || 'Login failed');
            }
        } catch (e) {
            setAuthError('Network error. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleRegister = async () => {
        setAuthError('');
        if (password.length < 6) { setAuthError('Password must be at least 6 characters.'); return; }
        setAuthLoading(true);
        try {
            const finalDisplayName = displayName.trim() || username;
            const result = await PlayFabAPI.register(username, password, finalDisplayName, registerEmail.trim() || undefined);
            if (result.code === 200 && result.data) {
                const userData = {
                    playFabId: result.data.PlayFabId,
                    username: username,
                    sessionTicket: result.data.SessionTicket,
                    displayName: finalDisplayName
                };
                setUser(userData);
                setCurrentDisplayName(finalDisplayName);
                localStorage.setItem('playfabUser', JSON.stringify(userData));
                await PlayFabAPI.updateUserData(userData.sessionTicket, { displayName: finalDisplayName });
                await loadPlayerData(userData);
                setGameState('menu');
            } else {
                setAuthError(result.errorMessage || 'Registration failed');
            }
        } catch (e) {
            setAuthError('Network error. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        setAuthError('');
        setForgotSuccess('');
        if (!forgotEmail.trim()) { setAuthError('Please enter your email address.'); return; }
        setAuthLoading(true);
        try {
            const result = await PlayFabAPI.sendPasswordReset(forgotEmail.trim());
            if (result.code === 200) {
                setForgotSuccess('Password reset email sent! Check your inbox.');
                setForgotEmail('');
            } else {
                setAuthError(result.errorMessage || 'Could not send reset email. Please check the address and try again.');
            }
        } catch (e) {
            setAuthError('Network error. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const confirmLogout = () => {
        localStorage.removeItem('playfabUser');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('savedUsername');
        localStorage.removeItem('savedPassword');
        setUser(null);
        setUsername('');
        setPassword('');
        setDisplayName('');
        setRegisterEmail('');
        setForgotEmail('');
        setForgotSuccess('');
        setIsDevUser(false);
        setGameState('auth');
        setShowLogoutConfirm(false);
        setShowAccountMenu(false);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // SETTINGS
    // ─────────────────────────────────────────────────────────────────────────

    const saveSettings = async () => {
        if (!user) return;
        try {
            if (newDisplayName.trim() && newDisplayName.trim() !== user.displayName) {
                const result = await PlayFabAPI.updateDisplayName(user.sessionTicket, newDisplayName.trim());
                if (result.code === 200) {
                    addNotification('✅ ' + t('notif_display_name_updated', 'Display name updated!'));
                    const updated = { ...user, displayName: newDisplayName.trim() };
                    setUser(updated);
                    localStorage.setItem('playfabUser', JSON.stringify(updated));
                    setCurrentDisplayName(newDisplayName.trim());
                    setNewDisplayName('');
                    await PlayFabAPI.updateUserData(user.sessionTicket, { displayName: newDisplayName.trim() });
                } else {
                    addNotification('❌ ' + t('notif_display_name_failed', 'Failed to update display name'));
                }
            }
            await PlayFabAPI.updateUserData(user.sessionTicket, { notificationsEnabled: notificationsEnabled.toString() });
            setShowSettings(false);
        } catch {
            addNotification('❌ ' + t('notif_settings_failed', 'Failed to save settings'));
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DAILY QUESTION HANDLER
    // ─────────────────────────────────────────────────────────────────────────

    const handleDailyQuestion = async (answerIndex) => {
        const isCorrect = answerIndex === dailyQuestion.correct;
        setShowDailyQuestion(false);
        setDailyCompleted(true);
        const today = new Date().toISOString().slice(0, 10);

        if (isCorrect) {
            const newCoins = coins + 50;
            setCoins(newCoins);
            addNotification('✅ ' + t('notif_daily_correct', 'Correct! +50 coins!'));
            if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { coins: newCoins.toString(), dailyQuestionCompleted: 'true', lastPlayDate: today });
        } else {
            addNotification('❌ ' + t('notif_daily_incorrect', 'Not quite! Better luck tomorrow.'));
            if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { dailyQuestionCompleted: 'true', lastPlayDate: today });
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // LEADERBOARD
    // ─────────────────────────────────────────────────────────────────────────

    const loadGlobalLeaderboard = async (diff) => {
        if (!user || !difficulties[diff]?.leaderboard) return;
        try {
            const result = await PlayFabAPI.getLeaderboard(user.sessionTicket, difficulties[diff].leaderboard, 100);
            if (result.code === 200 && result.data?.Leaderboard) {
                const entries = result.data.Leaderboard.map(e => ({
                    rank: e.Position + 1, username: e.DisplayName || e.PlayFabId,
                    score: e.StatValue, playFabId: e.PlayFabId
                }));
                setGlobalLeaderboard(entries);
                const me = result.data.Leaderboard.find(e => e.PlayFabId === user.playFabId);
                if (me) {
                    setMyRank(me.Position + 1);
                    if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { lastRank: (me.Position + 1).toString() });
                }
            }
        } catch { }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // SHOP
    // ─────────────────────────────────────────────────────────────────────────

    const purchaseItem = async (item) => {
        const hasHardness = activeBoosts.some(b => b.id === 'hardness_potion');
        const hasBoost = activeBoosts.some(b => b.id === 'boost_1_5' || b.id === 'boost_2');
        const hasGenie = activeBoosts.some(b => b.id === 'genie_1' || b.id === 'genie_2' || b.id === 'genie_3');

        const conflict = (
            (item.id === 'hardness_potion' && (hasHardness || hasBoost || hasGenie)) ||
            ((item.id === 'boost_1_5' || item.id === 'boost_2') && (hasHardness || hasGenie)) ||
            ((item.id === 'genie_1' || item.id === 'genie_2' || item.id === 'genie_3') && (hasGenie || hasHardness || hasBoost)) ||
            (item.id === 'boost_1_5' && activeBoosts.some(b => b.id === 'boost_1_5' || b.id === 'boost_2')) ||
            (item.id === 'boost_2' && activeBoosts.some(b => b.id === 'boost_2')) ||
            (item.id === 'iso_certificate' && inventory.iso_certificate)
        );

        if (conflict) { setShowIncompatWarn(item.id); setTimeout(() => setShowIncompatWarn(null), 5000); return; }
        if (coins < item.price) { addNotification('❌ ' + t('notif_not_enough_coins', 'Not enough coins!')); return; }

        const newCoins = coins - item.price;
        setCoins(newCoins);

        if (item.id === 'streak_freeze') {
            const newInv = { ...inventory, streak_freeze: (inventory.streak_freeze || 0) + 1 };
            setInventory(newInv);
            addNotification('✅ ' + t('notif_purchased', 'Purchased {item}!').replace('{item}', item.name));
            createConfetti();
            if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { coins: newCoins.toString(), inventory: JSON.stringify(newInv) });

        } else if (item.id === 'iso_certificate') {
            const newInv = { ...inventory, iso_certificate: true };
            setInventory(newInv);
            if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { coins: newCoins.toString(), inventory: JSON.stringify(newInv) });

            const confetti = createCertificateConfetti();
            setTimeout(() => {
                confetti.stop();
                const overlay = document.createElement('div');
                overlay.style.cssText = 'position:fixed;inset:0;background:black;opacity:0;z-index:10000;pointer-events:none;transition:opacity 1s ease-in-out;';
                document.body.appendChild(overlay);
                requestAnimationFrame(() => { overlay.style.opacity = '1'; });
                setTimeout(() => {
                    setShowCredits(true);
                    confetti.elements.forEach(el => el.remove());
                    setTimeout(() => { overlay.style.opacity = '0'; setTimeout(() => overlay.remove(), 1000); }, 100);
                }, 1000);
            }, 3500);

        } else {
            // timed boost
            let newBoosts = activeBoosts.filter(b => item.id === 'boost_2' && b.id === 'boost_1_5' ? false : true);
            newBoosts.push({ id: item.id, name: item.name, expiresAt: Date.now() + item.duration });
            setActiveBoosts(newBoosts);
            addNotification('✅ ' + t('notif_activated', '{item} activated!').replace('{item}', item.name));
            createConfetti();
            if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { coins: newCoins.toString(), activeBoosts: JSON.stringify(newBoosts) });
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // GAME FLOW
    // ─────────────────────────────────────────────────────────────────────────

    const startGame = (diffId) => {
        setDifficulty(diffId);
        setShowReadyScreen(true);
    };

    const beginGame = () => {
        const diff = difficulties[difficulty];
        if (!diff) return;
        const pool = questionPools[difficulty] || [];
        if (pool.length === 0) { alert(t('error_no_questions', 'No questions loaded!')); setGameState('menu'); return; }

        const shuffled = shuffleArray(pool).slice(0, diff.questions).map(q => {
            const withIdx = q.options.map((text, i) => ({ text, i }));
            const shuffledOpts = shuffleArray(withIdx);
            return {
                q: q.q,
                options: shuffledOpts.map(o => o.text),
                correct: shuffledOpts.findIndex(o => o.i === q.correct),
            };
        }).filter(q => q.correct !== -1);

        setQuestionSet(shuffled);
        setCurrentQuestion(0);
        setScore(0);
        setCorrectAnswers(0);
        setTotalTime(0);
        setStreak(0);
        setMaxStreak(0);
        setTimeLeft(diff.time);
        setSelectedAnswer(null);
        setShowResult(false);
        setShowReadyScreen(false);
        setGameHistory([]);
        setGameState('playing');
    };

    const handleAnswer = (answerIndex) => {
        if (showResult || !difficulty) return;
        setSelectedAnswer(answerIndex);
        setShowResult(true);

        const q = questionSet[currentQuestion];
        const isCorrect = answerIndex === q.correct;
        const diff = difficulties[difficulty];
        const timeTaken = diff.time - timeLeft;

        setGameHistory(prev => [...prev, { question: q.q, options: q.options, userAnswer: answerIndex, correctAnswer: q.correct, isCorrect }]);

        let newScore = score;
        let newStreak = streak;

        if (isCorrect) {
            const base = Math.max(100, 1000 - timeTaken * 100);
            const points = Math.floor((base + streak * 50) * diff.multiplier * getActiveMultiplier());
            newScore = score + points;
            newStreak = streak + 1;
            setScore(newScore);
            setCorrectAnswers(c => c + 1);
            setStreak(newStreak);
            if (newStreak > maxStreak) setMaxStreak(newStreak);
            setShowStreakAnim(true);
            setTimeout(() => setShowStreakAnim(false), 1000);
        } else {
            setStreak(0);
        }

        setTotalTime(t => t + timeTaken);

        setTimeout(() => {
            if (currentQuestion < diff.questions - 1) {
                setCurrentQuestion(c => c + 1);
                setTimeLeft(diff.time);
                setSelectedAnswer(null);
                setShowResult(false);
            } else {
                endGame(isCorrect, newScore);
            }
        }, 1500);
    };

    const endGame = async (lastCorrect, finalScore) => {
        if (!difficulty) return;
        const diff = difficulties[difficulty];
        const finalCorrect = lastCorrect ? correctAnswers + 1 : correctAnswers;
        const accuracy = Math.round((finalCorrect / diff.questions) * 100);

        const diffOrder = Object.keys(difficulties).filter(k => k !== 'certification');
        const idx = diffOrder.indexOf(difficulty);
        setRecommendedDiff(idx < diffOrder.length - 1 ? diffOrder[idx + 1] : null);

        // result message
        const prefix = accuracy < 20 ? 'result_poor_' : accuracy < 50 ? 'result_fair_' : accuracy < 80 ? 'result_good_' : 'result_excellent_';
        const pool = Object.entries(appText).filter(([k]) => k.startsWith(prefix)).map(([, v]) => v);
        setResultMessage(pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : '');

        setScore(finalScore);
        setCorrectAnswers(finalCorrect);
        setGameState('results');

        if (!diff.leaderboard) return;

        // submit score
        try {
            const canUpdate = await (async () => {
                const r = await PlayFabAPI.getLeaderboard(user.sessionTicket, diff.leaderboard, 100);
                if (r.code !== 200 || !r.data?.Leaderboard) return true;
                const entry = r.data.Leaderboard.find(e => e.PlayFabId === user?.playFabId);
                return finalScore > (entry?.StatValue || 0);
            })();

            if (canUpdate) {
                await PlayFabAPI.updateStats(user.sessionTicket, [{ StatisticName: diff.leaderboard, Value: finalScore }]);
                addNotification('🏆 ' + t('notif_new_high_score', 'New high score on {mode}: {score}!').replace('{mode}', diff.name).replace('{score}', finalScore.toLocaleString()));
            } else {
                addNotification(t('notif_existing_higher', 'Your existing score is higher — no update made.'));
            }
        } catch { }

        const coinsEarned = Math.floor(finalScore / 1000) * 10;
        if (coinsEarned > 0) {
            const newCoins = coins + coinsEarned;
            setCoins(newCoins);
            addNotification('+' + coinsEarned + ' ' + t('notif_coins_earned', 'coins earned!'));
        }

        // streak
        const today = new Date().toISOString().slice(0, 10);
        if (lastPlayDate !== today) {
            let newStreak = dailyStreak;
            if (!lastPlayDate) {
                newStreak = 1;
                addNotification(t('notif_streak_started', 'Streak started! 🔥'));
            } else {
                const days = Math.floor((new Date(today) - new Date(lastPlayDate)) / 86400000);
                if (days === 1) {
                    newStreak = dailyStreak + 1;
                    addNotification(t('notif_streak_continued', '{streak} day streak! 🔥').replace('{streak}', newStreak));
                } else if (days > 1) {
                    const missed = days - 1;
                    if (inventory.streak_freeze > 0 && missed <= inventory.streak_freeze) {
                        const newInv = { ...inventory, streak_freeze: inventory.streak_freeze - missed };
                        setInventory(newInv);
                        newStreak = dailyStreak + 1;
                        addNotification(t('notif_streak_freeze_used', 'Used {n} Streak Freeze(s)! Streak continues at {streak}! 🔥').replace('{n}', missed).replace('{streak}', newStreak));
                        if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { inventory: JSON.stringify(newInv) });
                    } else {
                        newStreak = 1;
                        addNotification(t('notif_streak_reset', 'Streak reset. New streak started! 🔥').replace('{days}', missed));
                    }
                }
            }
            setDailyStreak(newStreak);
            setLastPlayDate(today);
            if (user) await PlayFabAPI.updateUserData(user.sessionTicket, { dailyStreak: newStreak.toString(), lastPlayDate: today });
        }

        // personal leaderboard
        const newEntry = { score: finalScore, correct: finalCorrect, total: diff.questions, accuracy, difficulty, maxStreak, date: new Date().toLocaleDateString(), timestamp: Date.now() };
        const newLB = [...leaderboard, newEntry].sort((a, b) => b.score - a.score).slice(0, 20);
        setLeaderboard(newLB);
        if (user) {
            const nc = coins + Math.floor(finalScore / 1000) * 10;
            await PlayFabAPI.updateUserData(user.sessionTicket, { personalLeaderboard: JSON.stringify(newLB), coins: nc.toString() }).catch(() => { });
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DEV MODE
    // ─────────────────────────────────────────────────────────────────────────

    const simulateDays = async (n) => {
        if (!user) return;
        const cur = lastPlayDate || new Date().toISOString().slice(0, 10);
        const d = new Date(cur);
        d.setDate(d.getDate() + n);
        const sim = d.toISOString().slice(0, 10);
        setLastPlayDate(sim);
        await PlayFabAPI.updateUserData(user.sessionTicket, { lastPlayDate: sim, dailyQuestionCompleted: 'false' });
        addNotification(`⏰ DEV: Time set to ${sim}`);
    };

    const resetProgress = async () => {
        if (!user || !window.confirm('Reset ALL progress?')) return;
        setDailyStreak(0); setLastPlayDate(null); setDailyCompleted(false);
        setCoins(0); setInventory({ streak_freeze: 0 }); setActiveBoosts([]); setMyRank(null);
        await PlayFabAPI.updateUserData(user.sessionTicket, { dailyStreak: '0', lastPlayDate: '', dailyQuestionCompleted: 'false', coins: '0', inventory: JSON.stringify({ streak_freeze: 0 }), activeBoosts: JSON.stringify([]), lastRank: '0' });
        addNotification('🔄 DEV: Progress reset!');
        setTimeout(() => window.location.reload(), 1000);
    };

    const awardCoins = async () => {
        const amount = parseInt(devAwardAmount);
        if (isNaN(amount) || amount <= 0) { addNotification('⚠️ Invalid amount'); return; }
        const nc = coins + amount;
        setCoins(nc);
        await PlayFabAPI.updateUserData(user.sessionTicket, { coins: nc.toString() });
        addNotification(`✅ DEV: Awarded ${amount} coins`);
        setDevAwardAmount('');
    };

    // ─────────────────────────────────────────────────────────────────────────
    // ASSET HELPER
    // ─────────────────────────────────────────────────────────────────────────

    const assetUrl = (key, fallback = '') => {
        const file = assetPaths[key] || fallback;
        return file ? `${process.env.PUBLIC_URL}/assets/${file}` : '';
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DIFFICULTY BADGE
    // ─────────────────────────────────────────────────────────────────────────

    const DifficultyBadge = ({ diffId }) => {
        const diff = difficulties[diffId];
        if (!diff) return null;
        const Icon = diff.icon;
        return (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold text-white" style={{ backgroundColor: diff.bgColor }}>
                <Icon className="w-4 h-4" />
                {diff.name}
            </span>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    const NotificationStack = () => (
        <>
            {notifications.map((n, i) => (
                <div key={n.id} style={{ top: `${16 + i * 72}px` }} className="fixed right-4 bg-gray-900 text-white border border-gray-700 px-6 py-3 rounded-full shadow-2xl z-50 flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    <span>{n.message}</span>
                </div>
            ))}
        </>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // LOADING SCREEN
    // ─────────────────────────────────────────────────────────────────────────

    if (!configLoaded || !questionsLoaded || !dailyQuestionChecked || !minLoadingComplete) {
        const loadingText = !configLoaded
            ? t('loading_text_colors', 'Loading...')
            : !questionsLoaded
                ? t('loading_text_questions', 'Loading Questions...')
                : t('loading_text_general', 'Innovating...');

        return (
            <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: colors.loading_bg || '#F9F7ED' }}>
                {assetPaths.logo && <img src={assetUrl('logo')} alt={t('app_name', 'Quiz')} className="w-48 h-auto mb-8" />}
                {assetPaths.loading_gif && <img src={assetUrl('loading_gif')} alt="Loading" className="w-32 h-32 mb-6" />}
                <p className="text-2xl font-bold animate-pulse" style={{ color: c('text_primary', '#454443'), fontFamily: 'Century Gothic, sans-serif' }}>
                    {loadingText}
                </p>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // CREDITS
    // ─────────────────────────────────────────────────────────────────────────

    if (showCredits) {
        return (
            <div className="min-h-screen flex items-start justify-center overflow-hidden" style={{ backgroundColor: colors.bg_primary || '#F9F7ED' }} onClick={() => setShowSkipButton(true)}>
                <div className="w-full max-w-4xl px-8">
                    <div style={{ animation: creditsComplete ? 'scrollUp 90s linear forwards paused' : 'scrollUp 90s linear forwards', transform: 'translateY(100vh)' }} className="space-y-16 py-32">
                        <div className="text-center">
                            <div className="bg-gradient-to-br from-amber-500 to-yellow-600 rounded-3xl w-48 h-48 flex items-center justify-center mx-auto mb-8">
                                <Award className="w-32 h-32 text-white" />
                            </div>
                            <h1 className="text-7xl font-black mb-4" style={{ color: c('orange_secondary', '#C87533') }}>{t('credits_game_complete', 'GAME COMPLETE')}</h1>
                            <h2 className="text-5xl font-bold" style={{ color: c('text_primary', '#454443') }}>{t('credits_master_title', 'Master of Innovation')}</h2>
                        </div>
                        <div className="text-center space-y-6 max-w-2xl mx-auto">
                            <p className="text-2xl" style={{ color: c('text_primary', '#454443') }}>{t('credits_intro_1', '')}</p>
                            <p className="text-2xl" style={{ color: c('text_primary', '#454443') }}>{t('credits_intro_2', '')}</p>
                            <p className="text-2xl font-semibold" style={{ color: c('text_primary', '#454443') }}>{t('credits_intro_3', '')}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-4xl font-bold mb-4" style={{ color: c('text_primary', '#454443') }}>{t('app_name', 'Quiz App')}</p>
                            <p className="text-xl" style={{ color: c('text_primary', '#454443'), opacity: 0.7 }}>Made with ❤️ by Ampersand Associates</p>
                        </div>
                    </div>
                </div>
                {showSkipButton && (
                    <button onClick={() => { setShowCredits(false); document.body.style.overflow = 'auto'; }}
                        className="fixed bottom-8 right-8 px-6 py-3 rounded-full font-bold text-white z-50"
                        style={{ backgroundColor: c('orange_primary', '#EA6133') }}>
                        {t('credits_skip_button', 'Skip')}
                    </button>
                )}
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // AUTH SCREEN
    // ─────────────────────────────────────────────────────────────────────────

    if (gameState === 'auth') {

        // Forgot password view
        if (authMode === 'forgot') {
            return (
                <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>
                    <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>
                        {assetPaths.lightbulb
                            ? <img src={assetUrl('lightbulb')} alt={t('app_name', 'Quiz')} className="w-24 h-24 mx-auto mb-6 object-contain" />
                            : assetPaths.logo
                                ? <img src={assetUrl('logo')} alt={t('app_name', 'Quiz')} className="w-40 h-auto mx-auto mb-6" />
                                : null
                        }
                        <h1 className="text-3xl font-black text-center mb-2" style={{ color: c('text_primary', '#454443') }}>Reset Password</h1>
                        <p className="text-center mb-6 opacity-70 text-sm" style={{ color: c('text_primary', '#454443') }}>
                            Enter your email address and we'll send you a link to reset your password.
                        </p>
                        <div className="space-y-4">
                            <input
                                type="email"
                                placeholder="Email address"
                                value={forgotEmail}
                                onChange={e => setForgotEmail(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
                                className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2 focus:ring-white/50"
                                style={{ backgroundColor: c('bg_primary', '#FBFAF3'), borderColor: 'rgba(255,255,255,0.2)', color: c('text_primary', '#454443') }}
                            />
                            {authError && <p className="text-red-400 text-sm text-center">{authError}</p>}
                            {forgotSuccess && <p className="text-green-500 text-sm text-center">{forgotSuccess}</p>}
                            <button
                                onClick={handleForgotPassword}
                                disabled={authLoading}
                                className="w-full py-3 rounded-full font-bold hover:scale-105 transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                                style={{ backgroundColor: c('create_account', '#F3A214'), color: c('text_primary', '#454443') }}
                            >
                                {authLoading ? 'Sending...' : 'Send Reset Email'}
                            </button>
                            <button
                                onClick={() => { setAuthMode('login'); setAuthError(''); setForgotEmail(''); setForgotSuccess(''); }}
                                className="w-full text-center text-sm opacity-70 hover:opacity-100 transition-opacity"
                                style={{ color: c('text_primary', '#454443') }}
                            >
                                ← Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>
                <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>

                    {/* Logo / lightbulb — matches original exactly */}
                    {assetPaths.lightbulb
                        ? <img src={assetUrl('lightbulb')} alt={t('app_name', 'Quiz')} className="w-24 h-24 mx-auto mb-6 object-contain" />
                        : assetPaths.logo
                            ? <img src={assetUrl('logo')} alt={t('app_name', 'Quiz')} className="w-40 h-auto mx-auto mb-6" />
                            : null
                    }

                    <h1 className="text-4xl font-black text-center mb-2" style={{ color: c('text_primary', '#454443') }}>
                        {t('app_name', 'Quiz App')}
                    </h1>
                    <p className="text-center mb-6 opacity-80" style={{ color: c('text_primary', '#454443') }}>
                        {t('login_subtitle', 'Sign in to compete globally!')}
                    </p>

                    {/* Login / Register tab switcher — matches original exactly */}
                    <div className="flex gap-2 mb-6">
                        <button
                            onClick={() => { setAuthMode('login'); setAuthError(''); }}
                            className={`flex-1 py-2 rounded-full font-bold transition-all ${authMode === 'login'
                                ? 'bg-white text-indigo-900'
                                : 'bg-white/20 hover:bg-white/30'}`}
                            style={{ color: authMode === 'login' ? '#1e1b4b' : c('text_primary', '#454443') }}
                        >
                            Login
                        </button>
                        <button
                            onClick={() => { setAuthMode('register'); setAuthError(''); }}
                            className={`flex-1 py-2 rounded-full font-bold transition-all ${authMode === 'register'
                                ? 'bg-white text-indigo-900'
                                : 'bg-white/20 hover:bg-white/30'}`}
                            style={{ color: authMode === 'register' ? '#1e1b4b' : c('text_primary', '#454443') }}
                        >
                            Register
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Username */}
                        <div>
                            <input
                                type="text"
                                placeholder="Username"
                                value={username}
                                onChange={e => setUsername(e.target.value.slice(0, 20))}
                                onKeyDown={e => e.key === 'Enter' && (authMode === 'login' ? handleLogin() : handleRegister())}
                                maxLength={20}
                                className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2 focus:ring-white/50"
                                style={{ backgroundColor: c('bg_primary', '#FBFAF3'), borderColor: 'rgba(255,255,255,0.2)', color: c('text_primary', '#454443') }}
                            />
                            <p className="text-xs mt-1 opacity-60" style={{ color: c('text_primary', '#454443') }}>Max 20 characters</p>
                        </div>

                        {/* Register-only fields */}
                        {authMode === 'register' && (
                            <>
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Display Name"
                                        value={displayName}
                                        onChange={e => setDisplayName(e.target.value.slice(0, 10))}
                                        maxLength={10}
                                        className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2 focus:ring-white/50"
                                        style={{ backgroundColor: c('bg_primary', '#FBFAF3'), borderColor: 'rgba(255,255,255,0.2)', color: c('text_primary', '#454443') }}
                                    />
                                    <p className="text-xs mt-1 opacity-60" style={{ color: c('text_primary', '#454443') }}>
                                        Max 10 characters (shown on leaderboards)
                                    </p>
                                </div>
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Email address (for password reset)"
                                        value={registerEmail}
                                        onChange={e => setRegisterEmail(e.target.value)}
                                        className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2 focus:ring-white/50"
                                        style={{ backgroundColor: c('bg_primary', '#FBFAF3'), borderColor: 'rgba(255,255,255,0.2)', color: c('text_primary', '#454443') }}
                                    />
                                    <p className="text-xs mt-1 opacity-60" style={{ color: c('text_primary', '#454443') }}>
                                        Used only for password recovery
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Password */}
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && (authMode === 'login' ? handleLogin() : handleRegister())}
                            className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2 focus:ring-white/50"
                            style={{ backgroundColor: c('bg_primary', '#FBFAF3'), borderColor: 'rgba(255,255,255,0.2)', color: c('text_primary', '#454443') }}
                        />
                        {authMode === 'register' && (
                            <p className="text-xs mt-1 opacity-60" style={{ color: c('text_primary', '#454443') }}>Min. 6 characters</p>
                        )}

                        {/* Error message */}
                        {authError && (
                            <p className="text-red-400 text-sm text-center">{authError}</p>
                        )}

                        {/* Remember me */}
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="rememberMe"
                                checked={rememberMe}
                                onChange={e => setRememberMe(e.target.checked)}
                                className="w-4 h-4 rounded"
                            />
                            <label htmlFor="rememberMe" className="text-sm" style={{ color: c('text_primary', '#454443') }}>Remember me</label>
                        </div>

                        {/* Submit button */}
                        <button
                            onClick={authMode === 'login' ? handleLogin : handleRegister}
                            disabled={authLoading}
                            className="w-full py-3 rounded-full font-bold hover:scale-105 transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                            style={{ backgroundColor: c('create_account', '#F3A214'), color: c('text_primary', '#454443') }}
                        >
                            {authLoading
                                ? (authMode === 'login' ? 'Logging in...' : 'Creating account...')
                                : (authMode === 'login' ? 'Login' : 'Create Account')
                            }
                        </button>

                        {/* Forgot password — login mode only */}
                        {authMode === 'login' && (
                            <button
                                onClick={() => { setAuthMode('forgot'); setAuthError(''); }}
                                className="w-full text-center text-sm opacity-70 hover:opacity-100 transition-opacity"
                                style={{ color: c('text_primary', '#454443') }}
                            >
                                Forgot password?
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // DAILY QUESTION
    // ─────────────────────────────────────────────────────────────────────────

    if (showDailyQuestion && dailyQuestion) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>
                <div className="rounded-3xl shadow-2xl p-8 max-w-2xl w-full border border-white/20" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>
                    <div className="rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6" style={{ background: `linear-gradient(to right, ${c('gradient_yellow_from')}, ${c('gradient_yellow_to')})` }}>
                        <Calendar className="w-12 h-12" style={{ color: c('text_primary', '#454443') }} />
                    </div>
                    <h1 className="text-3xl font-bold text-center mb-2" style={{ color: c('text_primary', '#454443') }}>{t('daily_challenge_title', 'Daily Challenge')}</h1>
                    <p className="text-center mb-6 opacity-80" style={{ color: c('text_primary', '#454443') }}>{t('daily_challenge_subtitle', 'Answer correctly for 50 bonus coins!')}</p>
                    <h2 className="text-2xl font-bold mb-6 leading-tight" style={{ color: c('text_primary', '#454443') }}>{dailyQuestion.q}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                        {dailyQuestion.options.map((opt, i) => (
                            <button key={i} onClick={() => handleDailyQuestion(i)}
                                className="px-6 py-5 rounded-full text-left font-bold transition-all border-2 hover:scale-105 shadow-lg"
                                style={{ backgroundColor: c('yellow_secondary', '#F3A214'), borderColor: c('box_outline', '#454443'), color: c('text_primary', '#454443') }}>
                                <div className="text-sm font-black mb-1">{['A', 'B', 'C', 'D'][i]}</div>
                                <div className="text-base">{opt}</div>
                            </button>
                        ))}
                    </div>
                    <button onClick={() => setShowDailyQuestion(false)} className="w-full py-3 rounded-full font-bold transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: c('text_primary', '#454443') }}>
                        {t('daily_skip_button', 'Skip for Today')}
                    </button>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // READY SCREEN
    // ─────────────────────────────────────────────────────────────────────────

    if (showReadyScreen && difficulty && difficulties[difficulty]) {
        const diff = difficulties[difficulty];
        const Icon = diff.icon;
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>
                <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20 text-center" style={{ backgroundColor: diff.bgColor }}>
                    <Icon className="w-32 h-32 mx-auto mb-6 text-white" />
                    <h1 className="text-5xl font-black text-white mb-4">{t('ready_title', 'Ready?')}</h1>
                    <div className="bg-white/10 rounded-2xl p-6 mb-6">
                        <h2 className="text-2xl font-bold text-white mb-4">{diff.name}</h2>
                        <div className="space-y-2 text-white/90">
                            <p className="text-lg">{diff.questions} {t('ready_questions_label', 'Questions')}</p>
                            <p className="text-lg">{diff.time === 90 ? `30 ${t('ready_minutes_label', 'minutes total')}` : `${diff.time} ${t('ready_seconds_label', 'seconds each')}`}</p>
                            <p className="text-lg">{diff.multiplier}× {t('ready_points_label', 'Points')}</p>
                            {!diff.leaderboard && <p className="text-yellow-300 text-sm mt-2">⚠️ {t('ready_no_leaderboard_warning', 'No leaderboard for this mode')}</p>}
                        </div>
                    </div>
                    <button onClick={beginGame} className="w-full text-white py-4 rounded-full font-bold text-xl hover:scale-105 transition-transform shadow-lg animate-gentlePulse border-2 mb-4"
                        style={{ backgroundColor: c('yellow_secondary', '#F3A214'), borderColor: c('box_outline', '#454443') }}>
                        {t('ready_start_button', 'Start')}
                    </button>
                    <button onClick={() => { setShowReadyScreen(false); setDifficulty(null); }}
                        className="w-full py-3 rounded-full font-bold text-white/80 hover:text-white transition-all bg-white/10 hover:bg-white/20">
                        {t('ready_back_button', 'Back to Menu')}
                    </button>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // MENU
    // ─────────────────────────────────────────────────────────────────────────

    if (gameState === 'menu') {
        const difficultyOrder = Object.keys(difficulties);
        const availableDiffs = isHardnessPotionActive()
            ? difficultyOrder.filter(d => d !== 'easy' && d !== 'medium')
            : difficultyOrder;

        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>
                <NotificationStack />

                {/* Logout confirm */}
                {showLogoutConfirm && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20 text-center" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>
                            <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: c('day_streak', '#f69a6f') }} />
                            <h2 className="text-2xl font-bold mb-4" style={{ color: c('text_primary', '#454443') }}>{t('logout_confirm_title', 'Logout?')}</h2>
                            <p className="mb-6 opacity-80" style={{ color: c('text_primary', '#454443') }}>{t('logout_confirm_body', 'Are you sure?')}</p>
                            <div className="flex gap-3">
                                <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 py-3 rounded-full font-bold bg-white/20" style={{ color: c('text_primary', '#454443') }}>{t('logout_confirm_no', 'Cancel')}</button>
                                <button onClick={confirmLogout} className="flex-1 py-3 rounded-full font-bold text-white hover:scale-105 transition-transform" style={{ backgroundColor: c('day_streak', '#f69a6f') }}>{t('logout_confirm_yes', 'Logout')}</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20 relative z-10" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>

                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="relative">
                            <button onClick={() => setShowAccountMenu(!showAccountMenu)} className="flex items-center gap-2 hover:bg-white/10 px-3 py-2 rounded-lg transition-colors">
                                <User className="w-5 h-5" style={{ color: c('text_primary', '#454443') }} />
                                <span className="font-bold" style={{ color: c('text_primary', '#454443') }}>{currentDisplayName || user?.username}</span>
                            </button>
                            {showAccountMenu && (
                                <div className="absolute top-12 left-0 bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl p-4 border border-gray-700 z-50 min-w-[180px]">
                                    <button onClick={() => { setShowAccountMenu(false); setShowSettings(true); }} className="w-full flex items-center gap-2 text-white hover:bg-white/10 px-3 py-2 rounded-lg mb-2">
                                        <Settings className="w-5 h-5" /><span>{t('settings_title', 'Settings')}</span>
                                    </button>
                                    <button onClick={() => { setShowAccountMenu(false); setShowLogoutConfirm(true); }} className="w-full flex items-center gap-2 text-red-400 hover:bg-white/10 px-3 py-2 rounded-lg">
                                        <LogOut className="w-5 h-5" /><span>{t('settings_logout_button', 'Logout')}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                        {assetPaths.logo && <img src={assetUrl('logo')} alt={t('app_name')} className="h-12 w-auto" />}
                    </div>

                    {/* Settings modal */}
                    {showSettings && (
                        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                            <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: c('text_primary', '#454443') }}>
                                        <Settings className="w-6 h-6" />{t('settings_title', 'Settings')}
                                    </h2>
                                    <button onClick={() => setShowSettings(false)}><X className="w-6 h-6" style={{ color: c('text_primary', '#454443') }} /></button>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <label className="font-semibold mb-2 block" style={{ color: c('text_primary', '#454443') }}>{t('settings_display_name_label', 'Display Name')}</label>
                                        <input type="text" placeholder={currentDisplayName || user?.username} value={newDisplayName} onChange={e => setNewDisplayName(e.target.value.slice(0, 10))} maxLength={10}
                                            className="w-full px-4 py-3 rounded-full bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2" />
                                        <p className="text-sm mt-2 opacity-60" style={{ color: c('text_primary', '#454443') }}>{t('settings_display_name_hint', 'Max 10 characters')}</p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold" style={{ color: c('text_primary', '#454443') }}>{t('settings_notifications_label', 'Notifications')}</span>
                                        <input type="checkbox" checked={notificationsEnabled} onChange={e => setNotificationsEnabled(e.target.checked)} className="w-5 h-5" />
                                    </div>

                                    {/* Dev mode */}
                                    {isDevUser && (
                                        <div className="border border-red-400/30 rounded-2xl p-4 space-y-3 bg-red-500/5">
                                            <p className="font-bold text-red-400">⚠️ Dev Mode</p>
                                            <div className="flex gap-2">
                                                <button onClick={() => simulateDays(1)} className="flex-1 bg-red-500/20 text-red-300 py-2 rounded-lg text-sm font-bold">+1 Day</button>
                                                <button onClick={() => simulateDays(2)} className="flex-1 bg-red-500/20 text-red-300 py-2 rounded-lg text-sm font-bold">+2 Days</button>
                                                <button onClick={resetProgress} className="flex-1 bg-red-500/20 text-red-300 py-2 rounded-lg text-sm font-bold">Reset All</button>
                                            </div>
                                            <div className="flex gap-2">
                                                <input type="number" placeholder="Coins to award" value={devAwardAmount} onChange={e => setDevAwardAmount(e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white text-sm border border-gray-600" />
                                                <button onClick={awardCoins} className="bg-green-500/20 text-green-300 px-4 py-2 rounded-lg text-sm font-bold">Award</button>
                                            </div>
                                        </div>
                                    )}

                                    <button onClick={saveSettings} className="w-full py-3 rounded-full font-bold text-white" style={{ backgroundColor: c('create_account', '#F3A214') }}>
                                        {t('settings_save_button', 'Save Settings')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Streak + Coins */}
                    <div className="flex gap-3 mb-6">
                        <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-full" style={{ backgroundColor: c('day_streak', '#f69a6f') }}>
                            <Flame className="w-5 h-5 text-white" />
                            <span className="font-bold text-white">{dailyStreak} {t('day_streak_label', 'Day Streak')}</span>
                        </div>
                        <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-full" style={{ backgroundColor: c('coins', '#F3A214') }}>
                            <Coins className="w-5 h-5 text-white" />
                            <span className="font-bold text-white">{coins.toLocaleString()} {t('coins_label', 'Coins')}</span>
                        </div>
                    </div>

                    {/* Active boosts */}
                    {activeBoosts.length > 0 && (
                        <div className="bg-white/10 rounded-2xl p-4 mb-6 border border-white/20">
                            <h3 className="font-bold mb-2" style={{ color: c('text_primary', '#454443') }}>{t('shop_active_boosts_title', 'Active Boosts')}</h3>
                            {activeBoosts.map(b => (
                                <div key={b.id} className="flex justify-between items-center text-sm mb-1" style={{ color: c('text_primary', '#454443') }}>
                                    <span>{b.name}</span>
                                    <span className="opacity-70">{t('shop_expires_label', 'expires in')} {Math.max(0, Math.ceil((b.expiresAt - Date.now()) / 60000))}m</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Difficulty buttons */}
                    <h2 className="text-xl font-bold mb-4" style={{ color: c('text_primary', '#454443') }}>{t('menu_choose_difficulty', 'Choose Difficulty')}</h2>
                    <div className="space-y-3 mb-6">
                        {availableDiffs.map(diffId => {
                            const diff = difficulties[diffId];
                            if (!diff) return null;
                            const Icon = diff.icon;
                            return (
                                <button key={diffId} onClick={() => startGame(diffId)}
                                    className="w-full flex items-center justify-between px-6 py-4 rounded-full font-bold text-white hover:scale-105 transition-transform shadow-lg border-2"
                                    style={{ backgroundColor: diff.bgColor, borderColor: c('box_outline', '#454443') }}>
                                    <div className="flex items-center gap-3">
                                        <Icon className="w-6 h-6" />
                                        <span className="text-lg">{diff.name}</span>
                                    </div>
                                    <div className="text-sm opacity-80">{diff.time}s · {diff.questions}Q · {diff.multiplier}×</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Shop */}
                    <button onClick={() => setShowShop(!showShop)}
                        className="w-full p-4 rounded-full font-bold text-white flex items-center justify-center gap-2 hover:scale-105 transition-transform mb-3 border-2"
                        style={{ backgroundColor: c('open_shop', '#F3A214'), borderColor: c('box_outline', '#454443') }}>
                        <Gift className="w-5 h-5" />
                        {showShop ? t('shop_close_button', 'Close Shop') : t('menu_open_shop', 'Open Shop')}
                    </button>

                    {showShop && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 mb-3">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold" style={{ color: c('text_primary', '#454443') }}>{t('shop_title', 'Shop')}</h2>
                                <div className="flex items-center gap-1" style={{ color: c('coins', '#F3A214') }}>
                                    <Coins className="w-5 h-5" />
                                    <span className="font-bold">{coins.toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {shopItems.map(item => {
                                    const Icon = item.icon;
                                    const canAfford = coins >= item.price;
                                    const isActive = activeBoosts.some(b => b.id === item.id);
                                    const isOwned = item.id === 'iso_certificate' && inventory.iso_certificate;
                                    const incompatible = showIncompatWarn === item.id;

                                    return (
                                        <div key={item.id} className={`p-4 rounded-2xl border ${canAfford ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/10'}`}>
                                            <div className="flex items-start gap-3 mb-3">
                                                <div className={`p-2 rounded-lg bg-gradient-to-r ${item.color} flex-shrink-0`}>
                                                    <Icon className="w-5 h-5" style={{ color: c('text_primary', '#454443') }} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-bold" style={{ color: c('text_primary', '#454443') }}>{item.name}</div>
                                                    <div className="text-sm opacity-70" style={{ color: c('text_primary', '#454443') }}>{item.description}</div>
                                                    {item.warning && <div className="text-red-400 text-xs mt-1 font-semibold">⚠️ {item.warning}</div>}
                                                    {incompatible && (
                                                        <div className="mt-2 text-red-400 text-xs font-bold flex items-center gap-1 bg-red-500/10 border border-red-400/30 rounded-lg px-2 py-1 animate-pulse">
                                                            <AlertCircle className="w-3 h-3" /><span>Cannot be purchased right now</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-1" style={{ color: c('coins', '#F3A214') }}>
                                                    <Coins className="w-4 h-4" />
                                                    <span className={`font-bold ${canAfford ? '' : 'text-red-400'}`}>{item.price.toLocaleString()}</span>
                                                </div>
                                                <button onClick={() => purchaseItem(item)} disabled={!canAfford || isOwned || isActive}
                                                    className={`px-4 py-2 rounded-lg font-bold transition-all ${canAfford && !isOwned && !isActive ? 'text-white hover:scale-105' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
                                                    style={canAfford && !isOwned && !isActive ? { backgroundColor: c('yellow_secondary', '#F3A214') } : {}}>
                                                    {isOwned ? 'Owned' : isActive ? 'Active' : canAfford ? t('shop_buy_button', 'Buy') : t('shop_not_enough_label', 'Not Enough')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Leaderboard */}
                    <button onClick={() => { setShowLeaderboard(!showLeaderboard); if (!showLeaderboard) loadGlobalLeaderboard(leaderboardDiff); }}
                        className="w-full p-4 rounded-full font-bold text-white flex items-center justify-center gap-2 hover:scale-105 transition-transform mb-6 border-2"
                        style={{ backgroundColor: c('leaderboard', '#66aba6'), borderColor: c('box_outline', '#454443') }}>
                        <TrendingUp className="w-5 h-5" />
                        {showLeaderboard ? t('menu_hide_leaderboard', 'Hide Global Leaderboard') : t('menu_view_leaderboard', 'View Global Leaderboard')}
                    </button>

                    {showLeaderboard && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 mb-6">
                            <div className="flex gap-2 mb-4 flex-wrap">
                                {Object.keys(difficulties).filter(k => difficulties[k].leaderboard).map(key => (
                                    <button key={key} onClick={() => { setLeaderboardDiff(key); loadGlobalLeaderboard(key); }}
                                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all min-w-[60px] ${leaderboardDiff === key ? 'bg-white text-gray-900' : 'bg-white/20'}`}
                                        style={{ color: leaderboardDiff === key ? '#454443' : c('text_primary', '#454443') }}>
                                        {difficulties[key].name}
                                    </button>
                                ))}
                            </div>
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: c('text_primary', '#454443') }}>
                                <Award className="w-5 h-5 text-yellow-400" />{t('menu_top_global', 'Top 100 Global')}
                            </h2>
                            {myRank && (
                                <div className="bg-blue-500/20 border border-blue-400/30 p-3 rounded-full mb-4">
                                    <div className="font-bold" style={{ color: c('text_primary', '#454443') }}>{t('menu_your_rank', 'Your Rank')}: #{myRank}</div>
                                </div>
                            )}
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {globalLeaderboard.length === 0
                                    ? <div className="text-center py-8 opacity-70" style={{ color: c('text_primary', '#454443') }}>{t('menu_no_scores', 'No scores yet.')}</div>
                                    : globalLeaderboard.map((e, i) => (
                                        <div key={i} className={`p-3 rounded-xl flex justify-between items-center ${e.playFabId === user?.playFabId ? 'bg-blue-500/30 border border-blue-400/50' : 'bg-white/10'}`}>
                                            <div className="flex items-center gap-3">
                                                <span className={`font-bold text-lg ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'opacity-60'}`} style={{ color: i > 2 ? c('text_primary', '#454443') : undefined }}>#{e.rank}</span>
                                                <span className="font-semibold" style={{ color: c('text_primary', '#454443') }}>{e.username}</span>
                                            </div>
                                            <span className="font-bold" style={{ color: c('text_primary', '#454443') }}>{e.score.toLocaleString()}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}

                    {/* Personal best */}
                    {leaderboard.length > 0 && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: c('text_primary', '#454443') }}>
                                <Award className="w-5 h-5 text-yellow-400" />{t('menu_personal_best_title', 'Your Personal Best')}
                            </h2>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {leaderboard.slice(0, 5).map((e, i) => (
                                    <div key={i} className="bg-white/10 p-3 rounded-xl flex justify-between items-center border border-white/10">
                                        <div>
                                            <div className="font-semibold" style={{ color: c('text_primary', '#454443') }}>{e.score.toLocaleString()} pts</div>
                                            <div className="text-xs opacity-70" style={{ color: c('text_primary', '#454443') }}>{e.correct}/{e.total} · {e.accuracy}% · {e.avgTime}s avg</div>
                                        </div>
                                        <DifficultyBadge diffId={e.difficulty} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PLAYING
    // ─────────────────────────────────────────────────────────────────────────

    if (gameState === 'playing') {
        if (!difficulty || !difficulties[difficulty] || questionSet.length === 0) {
            return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: c('bg_primary') }}><p style={{ color: c('text_primary') }}>{t('error_loading_game', 'Loading game...')}</p></div>;
        }
        const diff = difficulties[difficulty];
        const q = questionSet[currentQuestion];
        const progress = ((currentQuestion + 1) / diff.questions) * 100;
        const hasPotion = isHardnessPotionActive();
        const mult = getActiveMultiplier();

        return (
            <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>

                {/* Red vignette for hardness potion */}
                {hasPotion && (
                    <div className="fixed inset-0 pointer-events-none z-0">
                        {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((pos, i) => (
                            <div key={i} className={`absolute ${pos} w-64 h-64 bg-red-600/30 blur-3xl rounded-full`} />
                        ))}
                    </div>
                )}

                {/* Quit button */}
                <button onClick={() => { setGamePaused(true); setShowQuitConfirm(true); }}
                    className="fixed top-4 left-4 z-40 flex items-center gap-2 bg-red-500/80 hover:bg-red-500 px-4 py-2 rounded-full font-bold transition-all"
                    style={{ color: c('text_primary', '#454443') }}>
                    <Pause className="w-5 h-5" />
                    {t('game_quit_button', 'Quit')}
                </button>

                {/* Hardness notification */}
                <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-500/20 backdrop-blur-sm border border-red-400/50 px-6 py-3 rounded-full z-50 transition-opacity duration-1000 ${showHardnessNotif ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    <div className="flex items-center gap-2 text-red-300 font-bold">
                        <Skull className="w-5 h-5" />
                        {t('game_hardness_notification', 'Hardness Potion Active - 3× Points!')}
                    </div>
                </div>

                {/* Quit confirm */}
                {showQuitConfirm && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/20 text-center" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>
                            <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: c('day_streak', '#f69a6f') }} />
                            <h2 className="text-2xl font-bold mb-4" style={{ color: c('text_primary', '#454443') }}>{t('game_quit_confirm_title', 'Quit Game?')}</h2>
                            <p className="mb-6 opacity-80" style={{ color: c('text_primary', '#454443') }}>{t('game_quit_confirm_body', 'Your progress will not be saved.')}</p>
                            <div className="flex gap-3">
                                <button onClick={() => { setShowQuitConfirm(false); setGamePaused(false); }} className="flex-1 py-3 rounded-full font-bold bg-white/20" style={{ color: c('text_primary', '#454443') }}>{t('game_quit_confirm_no', 'Continue Playing')}</button>
                                <button onClick={() => { setShowQuitConfirm(false); setGamePaused(false); setGameState('menu'); }} className="flex-1 py-3 rounded-full font-bold text-white hover:scale-105 transition-transform" style={{ backgroundColor: c('day_streak', '#f69a6f') }}>{t('game_quit_confirm_yes', 'Quit')}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Game card */}
                <div className="rounded-3xl shadow-2xl p-6 max-w-2xl w-full border border-white/20 relative z-10" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>

                    {/* Top bar */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full border border-white/30">
                                {React.createElement(diff.icon, { className: "w-7 h-7", style: { color: c('text_primary', '#454443') } })}
                                <span className="font-bold" style={{ color: c('text_primary', '#454443') }}>{currentQuestion + 1}/{diff.questions}</span>
                            </div>
                            {mult > 1 && (
                                <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: `linear-gradient(to right, ${c('purple_multiplier', '#a855f7')}, ${c('pink_multiplier', '#ec4899')})` }}>
                                    <Zap className="w-5 h-5 text-white" />
                                    <span className="font-bold text-white">{mult}×</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full shadow-lg" style={{ background: `linear-gradient(to right, ${c('gradient_yellow_from', '#F5CE1E')}, ${c('gradient_yellow_to', '#F3A214')})` }}>
                            {assetPaths.lightbulb
                                ? <img src={assetUrl('lightbulb')} alt="Score" className="w-5 h-5 object-contain" />
                                : <Star className="w-5 h-5" style={{ color: c('text_primary', '#454443') }} />
                            }
                            <span className="font-bold" style={{ color: c('text_primary', '#454443') }}>{score.toLocaleString()}</span>
                        </div>
                    </div>

                    {/* Timer */}
                    <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold" style={{ color: c('text_primary', '#454443') }}>{t('game_time_remaining', 'Time Remaining')}</span>
                            <div className="flex items-center gap-2">
                                <Clock className={`w-4 h-4 ${timeLeft <= 2 ? 'text-red-400 animate-pulse' : ''}`} style={{ color: timeLeft > 2 ? c('text_primary', '#454443') : undefined }} />
                                <span className={`font-bold text-xl ${timeLeft <= 2 ? 'text-red-400 animate-pulse' : ''}`} style={{ color: timeLeft > 2 ? c('text_primary', '#454443') : undefined }}>{timeLeft}s</span>
                            </div>
                        </div>
                        <div className="h-4 rounded-full overflow-hidden border border-white/20" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                            <div className={`h-full transition-all ${timeLeft <= 2 ? 'animate-pulse' : ''}`}
                                style={{ width: `${(timeLeft / diff.time) * 100}%`, backgroundColor: timeLeft <= 2 ? c('orange_primary', '#EA6133') : c('green_primary', '#46A66E') }} />
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-2 rounded-full overflow-hidden mb-6 border border-white/20" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                        <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(to right, #60a5fa, #a855f7)' }} />
                    </div>

                    {/* Question */}
                    <h2 className="text-2xl font-bold mb-6 leading-tight" style={{ color: c('text_primary', '#454443') }}>{q.q}</h2>

                    {/* Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                        {q.options.map((opt, i) => {
                            let bgColor = difficulty === 'certification' ? diff.bgColor : c('bg_card', '#F9F7ED');
                            let textColor = difficulty === 'certification' ? 'white' : c('text_primary', '#454443');
                            if (showResult) {
                                if (i === q.correct) { bgColor = c('answer_correct', '#4ade80'); textColor = 'white'; }
                                else { bgColor = c('answer_incorrect', '#d5d3cc'); textColor = 'white'; }
                            }
                            return (
                                <button key={i} onClick={() => handleAnswer(i)} disabled={showResult}
                                    className={`px-6 py-5 ${difficulty === 'certification' ? 'rounded-2xl' : 'rounded-full'} text-left font-bold transition-all border-2 shadow-lg ${showResult ? 'cursor-not-allowed scale-95' : 'hover:scale-105 hover:shadow-2xl active:scale-95'}`}
                                    style={{ backgroundColor: bgColor, borderColor: c('box_outline', '#454443'), color: textColor }}>
                                    <div className="text-sm font-black mb-1 opacity-90">{['A', 'B', 'C', 'D'][i]}</div>
                                    <div className="text-base leading-snug">{opt}</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Streak bar */}
                    <div className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full shadow-lg transition-all duration-300 ${showStreakAnim ? 'animate-streakBarFlash' : ''} ${streak === 0 ? 'opacity-40' : 'opacity-100'}`}
                        style={{ backgroundColor: c('day_streak', '#f69a6f') }}>
                        <Flame className={`w-12 h-12 ${showStreakAnim ? 'animate-flameYellowFill' : ''}`} style={{ color: 'white', fill: c('day_streak', '#f69a6f'), stroke: 'white', strokeWidth: '1.5' }} />
                        <span className="font-bold text-white text-xl">{streak}× {t('game_streak_label', 'Streak')}</span>
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // RESULTS
    // ─────────────────────────────────────────────────────────────────────────

    if (gameState === 'results') {
        if (!difficulty || !difficulties[difficulty]) {
            return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: c('bg_primary') }}><p style={{ color: c('text_primary') }}>{t('error_loading_results', 'Loading results...')}</p></div>;
        }
        const diff = difficulties[difficulty];
        const accuracy = Math.round((correctAnswers / diff.questions) * 100);

        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c('bg_primary', '#F9F7ED') }}>
                <NotificationStack />
                <div className="rounded-3xl shadow-2xl p-8 max-w-2xl w-full border border-white/20" style={{ backgroundColor: c('bg_card', '#F9F7ED') }}>

                    {assetPaths.lightbulb
                        ? <img src={assetUrl('lightbulb')} alt="Result" className="w-28 h-28 mx-auto mb-6 object-contain" />
                        : <Award className="w-28 h-28 mx-auto mb-6 text-yellow-400" />
                    }

                    <h1 className="text-4xl font-bold text-center mb-4" style={{ color: c('text_primary', '#454443') }}>{resultMessage}</h1>
                    <div className="mb-6 text-center"><DifficultyBadge diffId={difficulty} /></div>

                    {/* Score card */}
                    <div className="my-8 p-6 rounded-2xl border border-yellow-400/30" style={{ background: 'linear-gradient(to right, rgba(245,206,30,0.2), rgba(243,162,20,0.2))' }}>
                        <div className="text-6xl font-black text-transparent bg-clip-text mb-1" style={{ backgroundImage: `linear-gradient(to right, ${c('gradient_yellow_from', '#F5CE1E')}, ${c('gradient_yellow_to', '#F3A214')})`, WebkitBackgroundClip: 'text' }}>
                            {score.toLocaleString()}
                        </div>
                        <div className="font-semibold text-lg opacity-90" style={{ color: c('text_primary', '#454443') }}>{t('results_total_score_label', 'Total Score')}</div>
                        {diff.leaderboard && <div className="text-green-400 text-sm mt-2">✓ {t('results_submitted_label', 'Submitted to Global Leaderboard')}</div>}
                        <div className="text-sm mt-1 flex items-center justify-center gap-1" style={{ color: c('text_primary', '#454443') }}>
                            <Coins className="w-4 h-4" />
                            +{Math.floor(score / 1000) * 10} {t('results_coins_earned_label', 'coins earned')}
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-8">
                        {[
                            { label: 'Accuracy', value: `${accuracy}%` },
                            { label: 'Correct', value: `${correctAnswers}/${diff.questions}` },
                            { label: 'Max Streak', value: `${maxStreak}×` },
                        ].map(s => (
                            <div key={s.label} className="bg-white/10 rounded-2xl p-4 text-center border border-white/20">
                                <div className="text-2xl font-black" style={{ color: c('text_primary', '#454443') }}>{s.value}</div>
                                <div className="text-xs font-semibold opacity-70" style={{ color: c('text_primary', '#454443') }}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Recommended difficulty */}
                    {recommendedDiff && difficulty !== 'certification' && (
                        accuracy >= 85 ? (
                            <div className="bg-green-400/20 p-4 rounded-2xl border border-green-400/30 mb-4">
                                <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5 text-green-400" /><span className="font-bold" style={{ color: c('text_primary', '#454443') }}>{t('results_ready_for_more', 'Ready for More?')}</span></div>
                                <p className="text-sm mb-3 opacity-90" style={{ color: c('text_primary', '#454443') }}>
                                    {t('results_upgrade_prompt', 'Great job! With {accuracy}% accuracy, you\'re ready to try {next} mode!').replace('{accuracy}', accuracy).replace('{next}', difficulties[recommendedDiff]?.name || '')}
                                </p>
                                <button onClick={() => { setRecommendedDiff(null); startGame(recommendedDiff); }}
                                    className="w-full py-3 rounded-full font-bold text-white hover:scale-105 transition-transform"
                                    style={{ backgroundColor: difficulties[recommendedDiff]?.bgColor || c('green_primary') }}>
                                    {t('results_try_next_button', 'Try {next} Mode Now').replace('{next}', difficulties[recommendedDiff]?.name || '')}
                                </button>
                            </div>
                        ) : (
                            <div className="bg-orange-400/20 p-4 rounded-2xl border border-orange-400/30 mb-4">
                                <div className="flex items-center gap-2 mb-2"><Target className="w-5 h-5 text-orange-400" /><span className="font-bold" style={{ color: c('text_primary', '#454443') }}>{t('results_keep_practicing', 'Keep Practicing!')}</span></div>
                                <p className="text-sm mb-3 opacity-90" style={{ color: c('text_primary', '#454443') }}>
                                    {t('results_practice_prompt', 'You scored {accuracy}%. Try to reach 85% before moving up!').replace('{accuracy}', accuracy).replace('{next}', difficulties[recommendedDiff]?.name || '')}
                                </p>
                                <button onClick={() => { setRecommendedDiff(null); startGame(difficulty); }}
                                    className="w-full py-3 rounded-full font-bold text-white hover:scale-105 transition-transform"
                                    style={{ backgroundColor: diff.bgColor }}>
                                    {t('results_try_again_button', 'Try {current} Again').replace('{current}', diff.name)}
                                </button>
                            </div>
                        )
                    )}

                    {/* Summary toggle */}
                    <button onClick={() => setShowSummary(!showSummary)}
                        className="w-full py-4 px-6 rounded-full font-bold text-lg flex items-center justify-center gap-2 hover:scale-105 transition-transform shadow-lg mb-4 text-white"
                        style={{ backgroundColor: c('leaderboard', '#66aba6') }}>
                        {showSummary ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
                        {showSummary ? t('results_hide_summary', 'Hide Summary') : t('results_view_summary', 'View Summary')}
                    </button>

                    {showSummary && (
                        <div className="bg-white/10 rounded-2xl p-5 border border-white/20 mb-6 max-h-96 overflow-y-auto">
                            <h3 className="text-xl font-bold mb-4" style={{ color: c('text_primary', '#454443') }}>{t('results_summary_title', 'Question Summary')}</h3>
                            <div className="space-y-4">
                                {gameHistory.map((entry, i) => (
                                    <div key={i} className={`p-4 rounded-2xl border-2 ${entry.isCorrect ? 'bg-green-500/10 border-green-400/30' : 'bg-red-500/10 border-red-400/30'}`}>
                                        <div className="flex items-start gap-2 mb-2">
                                            <span className="font-bold text-sm flex-shrink-0" style={{ color: c('text_primary', '#454443') }}>Q{i + 1}:</span>
                                            <span className="text-sm opacity-90" style={{ color: c('text_primary', '#454443') }}>{entry.question}</span>
                                        </div>
                                        <div className={`text-sm font-semibold ${entry.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                                            {t('results_your_answer', 'Your answer')}: {entry.options[entry.userAnswer] || t('results_no_answer', 'No answer')}
                                        </div>
                                        {!entry.isCorrect && (
                                            <div className="text-green-400 text-sm font-semibold">
                                                {t('results_correct_answer', 'Correct answer')}: {entry.options[entry.correctAnswer]}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button onClick={() => setGameState('menu')}
                        className="w-full py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 text-white hover:scale-105 transition-transform shadow-lg"
                        style={{ backgroundColor: c('yellow_secondary', '#F3A214') }}>
                        <RotateCcw className="w-6 h-6" />
                        {t('results_back_button', 'Back to Menu')}
                    </button>
                </div>
            </div>
        );
    }

    return null;
}